import {
  deskewedOverlayBox,
  mergeRects,
  RESTING_FILL,
  RESTING_OUTLINE,
} from "@/components/pdf-viewer/regionGeometry";
import type { ImageBand } from "@/app/recap/types/delineation.types";
import type { ImageContentRect } from "./ImageViewer";

/** Map a band's percent y/height onto the measured image content box. */
export function bandPixelRect(
  band: { y: number; height: number },
  content: ImageContentRect,
): { left: number; top: number; width: number; height: number } {
  return {
    left: content.left,
    width: content.width,
    top: content.top + (band.y / 100) * content.height,
    height: (band.height / 100) * content.height,
  };
}

/** Group the angled bands by their document angle (normally one group). */
export function groupBandsByAngle(bands: ImageBand[]): [number, ImageBand[]][] {
  const byAngle = new Map<number, ImageBand[]>();
  for (const band of bands) {
    if (!band.angle) continue;
    const group = byAngle.get(band.angle) ?? [];
    group.push(band);
    byAngle.set(band.angle, group);
  }
  return [...byAngle.entries()];
}

/** The deskewed page's bounding box, centered on the image content box —
 *  the overlay that, rotated by ``angle`` (clockwise degrees), maps deskewed
 *  band coordinates onto the tilted document as displayed. `ImageContentRect`
 *  and `Box` are structurally identical, so this is a thin adapter onto the
 *  shared geometry both photo paint and photo hit-test rely on. */
export function deskewedOverlayRect(
  content: ImageContentRect,
  angle: number,
): ImageContentRect {
  return deskewedOverlayBox(content, angle);
}

const flatPercentRect = (band: { y: number; height: number }) => ({
  x: 0,
  y: band.y,
  width: 100,
  height: band.height,
});

/**
 * The two highlight layers ImageViewer paints over its measured image content
 * box: the neutral "resting" layer (every flow's region, faint, shown while
 * the pointer is inside the preview) and the strong per-band hover bands — in
 * that order, so hover always paints over rest. Split out of ImageViewer
 * purely to keep that file under the 500-line limit (mirrors
 * `PdfHighlightLayers` being split out of `CustomPDFViewer` for the same
 * reason); the geometry itself is `regionGeometry.ts`'s job, this only paints
 * what it computes.
 *
 * Resting and strong bands are rendered via SEPARATE `.map()` expressions,
 * never concatenated into one array: band ids are unique but NOT stable
 * between the single-flow and multi-flow walks that feed the two lists, so
 * the same id can appear in both. React keys only need to be unique among
 * siblings, and separate map expressions keep them sibling-scoped.
 */
export function ImageHighlightLayers({
  content,
  imageBands,
  restingBands,
}: Readonly<{
  content: ImageContentRect;
  imageBands: ImageBand[];
  restingBands: ImageBand[];
}>) {
  // Overlapping bands are MERGED into one block per `mergeRects`, and that
  // block is ONE div carrying both the fill and the outline: the merge stops
  // overlapping bands stacking alpha into a darker (falsely "more important")
  // strip, and drawing the edge on the same shape stops a table rendering as a
  // ladder of doubled rules (design §2.4c).
  const restingFlatBlocks = mergeRects(
    restingBands.filter((band) => !band.angle).map(flatPercentRect),
  );
  const restingAngledGroups = groupBandsByAngle(restingBands);

  return (
    <>
      {/* Neutral resting layer — painted BEFORE the strong bands so hover
          always reads on top. */}
      {/* Keyed on geometry, not the map index: `mergeRects` returns
          non-overlapping blocks, so a block's y/height pair is unique within
          its layer and survives a re-merge that changes the block count. */}
      {restingFlatBlocks.map((rect) => {
        const box = bandPixelRect(rect, content);
        return (
          <div
            key={`resting-band-${rect.y}-${rect.height}`}
            data-testid="photo-resting-band"
            style={{
              position: "absolute",
              left: box.left,
              top: box.top,
              width: box.width,
              height: box.height,
              backgroundColor: RESTING_FILL,
              border: `1px solid ${RESTING_OUTLINE}`,
              pointerEvents: "none",
            }}
          />
        );
      })}
      {restingAngledGroups.map(([angle, bands]) => {
        const overlay = deskewedOverlayRect(content, angle);
        const blocks = mergeRects(bands.map(flatPercentRect));
        return (
          <div
            key={`resting-angled-${angle}`}
            data-testid="photo-resting-angled-overlay"
            style={{
              position: "absolute",
              left: overlay.left,
              top: overlay.top,
              width: overlay.width,
              height: overlay.height,
              transform: `rotate(${angle}deg)`,
              pointerEvents: "none",
            }}
          >
            {blocks.map((rect) => (
              <div
                key={`resting-band-${rect.y}-${rect.height}`}
                data-testid="photo-resting-band"
                style={{
                  position: "absolute",
                  left: 0,
                  width: overlay.width,
                  top: (rect.y / 100) * overlay.height,
                  height: (rect.height / 100) * overlay.height,
                  backgroundColor: RESTING_FILL,
                  border: `1px solid ${RESTING_OUTLINE}`,
                  pointerEvents: "none",
                }}
              />
            ))}
          </div>
        );
      })}
      {imageBands
        .filter((band) => !band.angle)
        .map((band) => {
          const rect = bandPixelRect(band, content);
          return (
            <div
              key={band.id}
              data-testid="photo-highlight-band"
              style={{
                position: "absolute",
                left: rect.left,
                width: rect.width,
                top: rect.top,
                height: rect.height,
                backgroundColor: `${band.color}26`,
                borderTop: `1px solid ${band.color}80`,
                borderBottom: `1px solid ${band.color}80`,
                pointerEvents: "none",
              }}
            />
          );
        })}
      {/* Tilted/rotated documents: bands live in the DESKEWED frame, so
          they are laid out inside an overlay sized to the deskewed page's
          bounding box (centered on the image content — the frames share
          their centre) and the whole overlay rotates by the document
          angle. Only the highlights rotate; the photo stays as displayed. */}
      {groupBandsByAngle(imageBands).map(([angle, bands]) => {
        const overlay = deskewedOverlayRect(content, angle);
        return (
          <div
            key={`angled-bands-${angle}`}
            data-testid="photo-highlight-angled-overlay"
            style={{
              position: "absolute",
              left: overlay.left,
              top: overlay.top,
              width: overlay.width,
              height: overlay.height,
              transform: `rotate(${angle}deg)`,
              pointerEvents: "none",
            }}
          >
            {bands.map((band) => (
              <div
                key={band.id}
                data-testid="photo-highlight-band"
                style={{
                  position: "absolute",
                  left: 0,
                  width: overlay.width,
                  top: (band.y / 100) * overlay.height,
                  height: (band.height / 100) * overlay.height,
                  backgroundColor: `${band.color}26`,
                  borderTop: `1px solid ${band.color}80`,
                  borderBottom: `1px solid ${band.color}80`,
                  pointerEvents: "none",
                }}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}
